import { useOrgStore } from '@/stores/org-store';
import { apiRequest } from '@/utils/api-request';
import {
  A2AIpAllowlist,
  UpdateA2AIpAllowlistRequest,
} from './a2a-ip-allowlist-types';

/**
 * Replace the org's allowlist and return the persisted config.
 *
 * Returns the server's response rather than the request: the API normalizes
 * entries (bare IPs -> /32 or /128, host bits masked) and stamps the audit
 * fields, so only the response describes what is actually stored. The caller
 * seeds the query cache with it, which is why an empty body throws instead of
 * resolving — TanStack Query treats `undefined` as a no-op, so returning
 * undefined would silently leave the page on stale state after a save.
 */
export const updateA2AIpAllowlist = async (
  request: UpdateA2AIpAllowlistRequest,
): Promise<A2AIpAllowlist> => {
  const orgId = useOrgStore.getState().currentOrg?.orgid;
  if (!orgId) {
    throw new Error('No organization selected');
  }

  const response = await apiRequest<A2AIpAllowlist>({
    url: `/organizations/${orgId}/a2a-ip-allowlist`,
    method: 'PUT',
    target: 'platform',
    body: request as unknown as Record<string, unknown>,
  });

  if (response?.detail) {
    throw new Error(response.detail || 'Failed to update A2A IP allowlist');
  }

  if (!response.data) {
    throw new Error('A2A IP allowlist save returned no configuration');
  }

  return response.data;
};
